import React, { ReactElement } from 'react';
import {
  Box,
  Column,
  Container,
  DeprecatedIcon,
  DeprecatedIconProps,
  Text,
  Tooltip,
} from '@fullstory/components';
import { handleInteractiveKeydown } from '@fullstory/util/a11y';

const DefaultPadding = 16;
const DataIconSize = 12;

/**
 * A styled string for use in the DetailsRow with restrited options.
 */
export interface StyledString {
  /**
   * The string content to be displayed.
   */
  body: string;
  /**
   * Minimal styling options for the string.
   */
  bodyVariant?: 'regular' | 'bold';
}

export interface EngagementMapsDetailsRowProps {
  /**
   * An optional icon to be displayed at the front (only name and color are used)
   */
  icon?: DeprecatedIconProps;
  /**
   * The label for the row (e.g. "Page: ")
   */
  label: StyledString;
  /**
   * The value for the row (e.g. "/search"). Use a StyledString for simple
   * cases, but supports ReactElements for customization.
   */
  value: StyledString | ReactElement;
  /**
   * The content for the tooltip surrounding the row.
   */
  tooltipContent?: ReactElement;
  /**
   * Optional props to pass to the Tooltip
   */
  tooltipProps?: Pick<
    React.ComponentProps<typeof Tooltip>,
    'disablePadding' | 'offset' | 'placement' | 'maxWidth' | 'variant'
  >;
  /**
   * An optional callback to handle when the row is clicked
   */
  onRowClick?: () => void;
}

/**
 * This component is a helper component for styling the row contents of the
 * EngagementMapsDetailsCard
 *
 * There are a few canned components that abstract even more complexity away
 * for most use cases (PageDefinitionRow, ClicksRow, DeviceRow, TimeFrameRow,
 * etc.)
 */
export const EngagementMapsDetailsRow: React.FC<EngagementMapsDetailsRowProps> = ({
  icon,
  label,
  value,
  tooltipContent,
  tooltipProps,
  onRowClick,
  ...restOfProps
}) => {
  const rowContents: ReactElement = (
    <Container verticalAlign="center">
      {icon && (
        <DeprecatedIcon
          size={DataIconSize}
          name={icon.name}
          color={icon.color ? icon.color : 'neutrals-n7'}
        />
      )}
      <Column>
        <Box data-fs="engagement-maps-details-card-row-key">
          <Text
            appearance={label.bodyVariant === 'bold' ? 'bodySmallBold' : 'bodySmall'}
            color={label.bodyVariant === 'bold' ? 'emphasized' : 'secondary'}
          >
            {label.body}
          </Text>
        </Box>
      </Column>
      <Container align="end">
        <Column>
          <Box data-fs="engagement-maps-details-card-row-value" maxWidth={250}>
            {'body' in value ? ( // is a StyledString
              <Text
                tag="div"
                noWrap
                appearance={label.bodyVariant === 'bold' ? 'bodySmallBold' : 'bodySmall'}
                color={label.bodyVariant === 'bold' ? 'emphasized' : 'default'}
              >
                {value.body}
              </Text>
            ) : (
              value
            )}
          </Box>
        </Column>
      </Container>
    </Container>
  );

  const content = onRowClick ? (
    <div
      role="button"
      tabIndex={0}
      style={{ cursor: 'pointer' }}
      onClick={onRowClick}
      onKeyDown={handleInteractiveKeydown(onRowClick)}
    >
      {rowContents}
    </div>
  ) : (
    <>{rowContents}</>
  );

  return (
    <Box
      data-component="EngagementMapsDetailsRow"
      data-fs="engagement-maps-details-card-row"
      paddingBottom={DefaultPadding}
      {...restOfProps}
    >
      {!tooltipContent ? (
        content
      ) : (
        <Tooltip
          placement="bottom-start"
          tooltipContent={tooltipContent}
          maxWidth={200}
          {...tooltipProps}
        >
          {content}
        </Tooltip>
      )}
    </Box>
  );
};
